import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Locale, SiteSettings, ThemeMarketItem } from '@icetus/contracts';
import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_LABEL,
  MEDIA_MIME,
  MAX_UPLOAD_BYTES,
  SITE_BUSINESS_LIMIT,
  SITE_COMPANY_LIMIT,
  SITE_TITLE_LIMIT,
  normalizeWhatsapp,
} from '@icetus/contracts';
import { Button } from '@icetus/ui';
import { themeSummaries, type ThemeSummary } from '@icetus/theme-kit';
import {
  ApiError,
  confirmMedia,
  createSite,
  fetchSiteList,
  fetchSiteSettings,
  fetchThemeMarket,
  presignMedia,
  putToStorage,
  saveSiteSettings,
} from '../api';
import { BrandGenerator } from '../components/brand-generator';
import { useConfirm } from '../components/confirm';
import { ThemeThumb } from '../components/theme-thumb';

/**
 * 完成屏。
 *
 * 站建完之后**只做两件事**：告诉他站在哪、告诉他接下来还差什么。
 * 原来只有一句"去页面内容发布"，客户回到控制台仍然不知道下一步先做哪个 ——
 * 而这三件事的先后是有讲究的：先定地址（要不要绑域名），再发布，最后填内容。
 *
 * 单独拆成组件是为了能单测：向导本体的完成态要跑完三步 HTTP 才到得了，
 * 想断言"这块文案还在"就只能把它拎出来。
 *
 * 域名这一段**只给文字 + 一个跳转**，不在向导里内嵌绑定表单：
 * 站点建好之后左侧菜单已经全展开了，绑定是「域名」页的活，
 * 在这里再实现一遍等于两套入口、两种错误提示。
 */
export function WizardDone({
  siteName,
  host,
  warn,
  emailVerified,
  onClose,
  onGo,
}: {
  siteName: string;
  host: string;
  warn: string | null;
  /**
   * 邮箱验证了吗。
   *
   * `POST /v1/domains` 挂了 `@RequireVerified()`。不给这个开关的话，
   * 刚注册、还没点验证邮件的人点「去绑定域名」会吃一个 403 ——
   * 而他在域名页看不出"要先验证邮箱"，只会以为功能坏了。
   */
  emailVerified: boolean;
  /** 「稍后再说」走它：关掉向导，回到「我的站点」 */
  onClose: () => void;
  onGo: (key: 'domains' | 'catalog') => void;
}) {
  return (
    <div className="wiz">
      <div className="wiz__panel wiz__panel--done">
        <div className="wiz__check" aria-hidden="true" />
        <h2 className="wiz__title">「{siteName}」建好了</h2>
        <p className="wiz__hint">
          地址是{' '}
          <a className="wiz__addr" href={`https://${host}`} target="_blank" rel="noreferrer">
            {host}
          </a>
          。<b>页面已经发布，现在打开就能看到</b>，可以直接把这个地址发给别人。
        </p>

        {warn ? <div className="notice notice--warn">{warn}</div> : null}

        <div className="wiz__next">
          <h3 className="wiz__nexttitle">接下来还有两件事</h3>
          <ol className="wiz__todo">
            <li className="wiz__todoitem">
              <span className="wiz__todonum" aria-hidden="true">
                1
              </span>
              <div className="wiz__todobody">
                <p className="wiz__todoname">
                  绑定自己的域名 <span className="wiz__tag">推荐</span>
                </p>
                <p className="wiz__tododesc">
                  绑上以后买家看到的是你的牌子，不是 <b>{host}</b>。在「域名」里填进去，
                  按提示到域名商那边加一条 CNAME 指向平台，验证通过后 https 会自动签发，一般几分钟。
                </p>
                <p className="wiz__tododesc">
                  <b>还没有域名？</b>先去阿里云、腾讯云或 Namecheap 买一个（一年几十块），回来再绑。
                  买的时候留意：阿里云和 DNSPod 的根域（不带 www 的那种）加不了 CNAME，这类先绑 www。
                </p>
                <p className="wiz__tododesc">
                  不着急也完全可以。先用上面这个地址用着，域名之后随时能补 ——
                  绑上以后已有内容和搜索引擎收录都不受影响。
                </p>
                {emailVerified ? null : (
                  <p className="wiz__tododesc">
                    <b>注意：</b>绑定域名前要先验证邮箱。验证邮件注册时已经发出，
                    回到「我的站点」顶部可以重发。
                  </p>
                )}
              </div>
            </li>

            <li className="wiz__todoitem">
              <span className="wiz__todonum" aria-hidden="true">
                2
              </span>
              <div className="wiz__todobody">
                <p className="wiz__todoname">
                  加第一个商品 <span className="wiz__tag">按需</span>
                </p>
                <p className="wiz__tododesc">
                  产品摆出来，首页的商品区块才有内容。只做公司介绍、不卖东西的话，
                  这一项可以跳过 —— 用不到的菜单项能收起来。
                </p>
                <button type="button" className="wiz__todogo" onClick={() => onGo('catalog')}>
                  去商品 →
                </button>
              </div>
            </li>
          </ol>
        </div>

        <div className="wiz__actions">
          {emailVerified ? (
            <Button variant="primary" onClick={() => onGo('domains')}>
              去绑定域名
            </Button>
          ) : (
            /* 未验证邮箱时这个按钮不往域名页送：那边会 403，而客户看不出原因 */
            <Button variant="primary" onClick={onClose}>
              先去验证邮箱
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            稍后再说，先看看站
          </Button>
        </div>
        <p className="wiz__tiny">
          这三件事在左侧菜单里都能找到，随时回来补。想换版式或补 Logo，去「站点信息」。
        </p>
      </div>
    </div>
  );
}

/**
 * 建站向导。
 *
 * 五步，**建站发生在第三步之后**：
 *
 *   1 站点名（内部） + 网站名（对外）
 *   2 公司名 / 主营业务 / 联系方式
 *   3 选版式  ──→ 这一步之后站点就已经存在了
 *   4 Logo（可以现场让 AI 画）
 *   5 开通语言
 *
 * ## 为什么建站要提前到中间
 *
 * 原来建站放在最后一步，代价是**第四第五步什么都做不了**：
 * `media.site_id` 与 `brand_art_jobs.site_id` 都是 `not null`，没有站点
 * 就没有地方挂 Logo 与出图任务。于是 AI 生成只能塞进"完成页"，
 * 客户看到的是"站建好了，现在给它配个标" —— 而他要的其实是
 * "我在建站过程中就把标配上"。
 *
 * 现在第 3 步一过就有 siteId，Logo 可以直接落库、AI 可以现场出图，
 * 语言也能在最后一步一次配齐，而不是建完再去站点信息里补。
 *
 * ## 中途离开会发生什么
 *
 * 第 3 步之前离开 = **什么都没发生**（没有任何写入，重进就是干净的）。
 * 第 3 步之后离开 = **站点已经存在**，它留在「我的站点」里，
 * 差的那两件事（Logo、语言）在「站点信息」里随时能补。
 * 这两句差别必须在确认弹窗里说清 —— 不说的话，客户会以为站没了。
 *
 * ## 三条硬约束
 *
 * 1. 第 3 步之前不写任何东西。Logo 选了文件也只是浏览器内存里的预览。
 * 2. 建站成功但**后面的写入失败不算失败**：站点已经能打开，报"创建失败"
 *    会让客户再点一次，结果建出第二个站。
 * 3. 已经建过站再返回上一步改版式，走的是 PATCH appearance，不是重新建站。
 */

const STEPS = ['基本信息', '公司与业务', '选个版式', 'Logo', '开通语言'] as const;

/**
 * 预选主语言。
 *
 * 中文浏览器给中文，其余给英文。**不写死英文**：客户会不会英文我们没法假设，
 * 而这一步选错，建出来的站连"这里该写什么"的提示都是他读不懂的句子。
 *
 * 用 `navigator.language` 而不是让客户先选一次：他对"默认语言"这个词没有概念，
 * 但看到「简体中文」被选中、旁边还能点 English，一眼就知道这里能改。
 */
function guessLocale(): Locale {
  const lang = typeof navigator === 'undefined' ? '' : String(navigator.language ?? '');
  return lang.toLowerCase().startsWith('zh') ? 'zh' : DEFAULT_LOCALE;
}

/** 建站之后留在内存里的结果 */
interface Created {
  siteId: string;
  host: string;
  /** 当前站点用的主题。返回上一步改版式时靠它判断要不要 PATCH */
  theme: string;
}

/**
 * 读宽高。
 *
 * 拿不到就算了：宽高只用于媒体库列表展示与去重判断，不该因为它
 * 让整个"传 Logo"失败（GIF 或异常文件解码会抛）。
 */
async function readSize(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    return size;
  } catch {
    return null;
  }
}

async function uploadLogo(tenantId: string, siteId: string, file: File): Promise<string> {
  const size = await readSize(file);
  const presigned = await presignMedia(tenantId, {
    siteId,
    filename: file.name || 'logo',
    mime: file.type,
    bytes: file.size,
    width: size?.width ?? null,
    height: size?.height ?? null,
    // 不算 checksum：Logo 只传一次，去重省不下什么，
    // 而 crypto.subtle 在 http 环境下根本不存在（见 ImageUploader 同一处注释）
    checksum: null,
  });
  if (presigned.reused) return presigned.media.url;
  await putToStorage(presigned.ticket, file);
  const media = await confirmMedia(tenantId, presigned.ticket.mediaId, size ?? undefined);
  return media.url;
}

export function SiteWizard({
  tenantId,
  emailVerified,
  onDone,
  onCancel,
  onNavigate,
  onDirtyChange,
}: {
  tenantId: string;
  /** 完成屏据此决定「去绑定域名」能不能点（未验证邮箱会 403） */
  emailVerified: boolean;
  /** 全部走完（或中途已经建好站、客户主动结束）后调用。父层据此重拉站点列表 */
  onDone: () => Promise<void> | void;
  onCancel: () => void;
  /**
   * 完成屏把客户送去别的页。
   *
   * 不传也能用（退回「返回我的站点」），只是完成屏的「去绑定域名」会变成
   * 一句指路。父层负责顺手把向导关掉 —— 它才知道向导是怎么挂上去的。
   */
  onNavigate?: (key: 'domains' | 'catalog') => void;
  /**
   * 草稿里有没有值得挽留的东西。
   *
   * 报到外壳上，是因为**离开这件事发生在外壳**（点侧栏别的一项）。
   * 向导自己拦不住那一下，只能提前说"我这儿有东西"。
   */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [step, setStep] = useState(0);

  /* ── 第 1 步 ── */
  const [siteName, setSiteName] = useState('');
  const [siteTitle, setSiteTitle] = useState('');

  /* ── 第 2 步 ── */
  const [company, setCompany] = useState('');
  const [business, setBusiness] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');

  /* ── 第 3 步 ── */
  const [theme, setTheme] = useState<string | null>(null);
  const [themes, setThemes] = useState<ThemeMarketItem[] | null>(null);
  const [themeError, setThemeError] = useState<string | null>(null);

  /* ── 第 4 步 ── */
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [brandOpen, setBrandOpen] = useState(false);
  /**
   * AI 那张图的地址。
   *
   * 单独存一份而不是只留一个布尔：客户要能**看见**刚画出来的东西。
   * 只说"已用 AI 生成"的话，他得等建完站去前台才知道长什么样。
   */
  const [logoAiUrl, setLogoAiUrl] = useState<string | null>(null);

  /* ── 第 1 步：主语言 ── */
  /**
   * 网站主要用什么语言。
   *
   * 预选**不写死英文**：客户是不是会英文，我们没法假设。原来注册时租户一律
   * 落 `en`，中文客户建完站打开首页看到的是英文模板，连"这里该写什么"
   * 的提示都读不懂 —— 那个站就废了。
   *
   * 判据用浏览器语言：中文浏览器默认中文，其余默认英文。客户就在这一步看着
   * 选中的是哪一项，改一下也不费事。
   */
  const [siteLocale, setSiteLocale] = useState<Locale>(() => guessLocale());

  /* ── 第 5 步 ── */
  const [defaultLocale, setDefaultLocale] = useState<Locale>(() => guessLocale());
  const [locales, setLocales] = useState<Locale[]>(() => [guessLocale()]);

  /* ── 全局 ── */
  const [error, setError] = useState<string | null>(null);
  /** 非阻断的提示：建站成功了，但某个后续写入没成 */
  const [warn, setWarn] = useState<string | null>(null);
  const [phase, setPhase] = useState<'edit' | 'creating' | 'done'>('edit');
  /** 创建过程中当前这一步在做什么。只影响那行文案 */
  const [progress, setProgress] = useState('');
  const [created, setCreated] = useState<Created | null>(null);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const { confirm, confirmDialog } = useConfirm();
  /**
   * 当前预览的 objectURL。
   *
   * 存 ref 而不是从 state 读：`revoke` 必须发生在**换图那一刻**，
   * 而 setState 是异步的，等它落定再去 revoke 上一条，拿到的是新的那条。
   */
  const previewRef = useRef<string | null>(null);

  const trimmedName = siteName.trim();
  const trimmedTitle = siteTitle.trim();
  const trimmedCompany = company.trim();
  const trimmedBusiness = business.trim();
  const whatsappDigits = normalizeWhatsapp(whatsapp);
  /** 填了号码但归一不出来（位数不对 / 以 0 开头）。这属于必须当场说清的错误 */
  const whatsappBad = whatsapp.trim() !== '' && whatsappDigits === null;

  /**
   * 每一步能不能往下一屏走。
   *
   * 只在第 1 步卡必填：站点名没有就建不出站。后面几屏全部可以空着过 ——
   * 客户来建站是想尽快看到自己的站，逼他先想清楚"主营业务"会直接筛掉一批人，
   * 而这些字段在「站点信息」里随时能补。
   */
  const canNext =
    step === 0
      ? trimmedName.length > 0
      : step === 1
        ? !whatsappBad
        : step === 2
          ? /*
             * 版式列表拉下来了才算"能继续"：还在加载时点下一步会建出一个
             * `theme: undefined` 的站（服务端按极光兜底），而客户以为自己选了。
             * 列表**为空**是例外 —— 提示里已经说了"先按极光建站"，
             * 这时候挡住他就等于让他卡死在这一屏。
             */
            theme !== null || themes?.length === 0
          : true;

  /* ── 版式列表 ────────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;
    fetchThemeMarket(tenantId)
      .then((view) => {
        if (cancelled) return;
        /**
         * 只留免费版式。
         *
         * 付费版式不是"看不见"，而是**在这一步没得选**：向导里没有
         * 下单与支付的路径，摆出来就是一张点了不能用、或者点下去
         * 被服务端 `theme_not_allowed` 拒掉的卡。要试付费版式，
         * 建好站之后去「主题市场」，那里有完整的订阅流程。
         */
        const free = view.items.filter((item) => !item.paid);
        setThemes(free);
        setTheme((current) => current ?? (free.find((i) => i.name === 'aurora')?.name ?? free[0]?.name ?? null));
      })
      .catch((err: unknown) => {
        if (!cancelled) setThemeError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const summaries = useMemo(() => {
    const map = new Map<string, ThemeSummary>();
    for (const summary of themeSummaries()) map.set(summary.name, summary);
    return map;
  }, []);

  /* ── 草稿脏值上报 ────────────────────────────────────────── */

  useEffect(() => {
    /**
     * 站一旦建出来，离开这件事就**不可逆**了 —— 站点已经落库，
     * 所以无论表单里还有没有字，都要拦一次。这不是"挽留输入"，
     * 是"提醒他站已经在里面了"。
     */
    onDirtyChange?.(
      created !== null || trimmedName.length > 0 || trimmedTitle.length > 0 || logoFile !== null,
    );
  }, [onDirtyChange, created, trimmedName, trimmedTitle, logoFile]);

  // 卸载时把最后一条 objectURL 还回去
  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    [],
  );

  /**
   * 换一步就回到顶部。
   *
   * 版式那一屏很长，客户选完最后一张卡时页面已经滚到底；点「下一步」后
   * 内容变短，滚动位置被浏览器夹在中间，新一屏的标题就落在一条不知所以的
   * 空白下面 —— 看起来像页面没加载完。
   */
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  const pickLogo = useCallback((file: File | null) => {
    setLogoError(null);
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
    if (!file) {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }
    /**
     * 两道前置校验放在客户端，理由只有一个：**立刻能说清**。
     * 服务端照样会拒（presign 会校验 mime 与大小），但那时客户已经
     * 等了两个来回，看到的还只是一句"上传失败"。
     */
    if (!(MEDIA_MIME as readonly string[]).includes(file.type)) {
      setLogoError('这个格式用不了。Logo 请用 PNG、JPG、WebP 或 AVIF。');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setLogoError(`这张图太大了（上限 ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB）。`);
      return;
    }
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setLogoFile(file);
    setLogoPreview(url);
    // 手选了文件，之前 AI 生成的那张就被顶掉了
    setLogoAiUrl(null);
  }, []);

  /* ── 第 3 步：建站 ──────────────────────────────────────── */

  /**
   * 把前两步收集到的东西写进站点设置。
   *
   * 单独一次 PATCH 而不是并进 createSite：建站接口只管"建出一个站"，
   * 客户信息是站点的属性、不是建站参数。分开的另一个好处是失败可重试 ——
   * 站点已经在了，重发这条 PATCH 不会多出一个站。
   */
  const persistBasics = async (siteId: string): Promise<SiteSettings> => {
    const hasContact = email.trim() !== '' || phone.trim() !== '' || whatsappDigits !== null;
    return saveSiteSettings(tenantId, siteId, {
      // 空串一律归一成 null：null 的含义是"没填过"，渲染层据此回退到租户名
      title: trimmedTitle || null,
      company: trimmedCompany || null,
      business: trimmedBusiness || null,
      ...(hasContact
        ? {
            contact: {
              email: email.trim() || null,
              phone: phone.trim() || null,
              whatsapp: whatsappDigits,
              // 向导只收这三样。地址、Telegram、微信二维码在「站点信息」里补 ——
              // 这里的 contact 是整体替换，必须把没问到的字段显式写 null
              address: null,
              telegram: null,
              wechatQrUrl: null,
            },
          }
        : {}),
    });
  };

  /**
   * 建站请求失败后回头确认"是不是其实已经建出来了"。
   *
   * 只认**同名**：这个名字是客户刚在第 1 步填的，而向导一旦建出站，
   * `created` 就非空，再点「下一步」走的是 PATCH —— 不可能再建一个同名的。
   * 不能图省事拿"最近一个站"顶替：老客户建第二个站时，列表里那个最近的
   * 可能是上一轮留下的，接上去等于把他送进别人的站。
   */
  const findCreatedSite = async (): Promise<{ siteId: string; host: string } | null> => {
    try {
      const { items } = await fetchSiteList(tenantId);
      const hit = items.find((item) => item.site.name.trim() === trimmedName);
      return hit ? { siteId: hit.site.id, host: hit.site.primaryHost } : null;
    } catch {
      // 连列表都读不到就按失败处理：带着"可能成功了"往下走，客户会进错站
      return null;
    }
  };

  const enterStepFour = async (): Promise<void> => {
    /*
     * 列表为空（接口挂了 / 全部下架）时用基础版兜底：这一屏的提示已经写明
     * "先按极光建站"，`theme` 却是 null 的话按钮会一直亮着却不干活。
     */
    const chosen = theme ?? 'aurora';

    /* 已经建过站：返回上一步改了版式就走 PATCH，绝不重建一个站 */
    if (created) {
      if (created.theme !== chosen) {
        setPhase('creating');
        setProgress('正在切换版式…');
        setError(null);
        try {
          /*
           * 只传 theme，变体交给服务端补 —— 新主题不一定有旧变体那个 key
           * （`meridian compact` 与 `aurora compact` 不是一回事），
           * 这里自己补一个猜测值只会撞 `invalid_settings`。
           */
          await saveSiteSettings(tenantId, created.siteId, { appearance: { theme: chosen } });
          setCreated({ ...created, theme: chosen });
        } catch (err) {
          /**
           * 版式没换成，但站还在。留在这一屏让他重试或换一套 ——
           * 比带着旧版式往下走更诚实：下一屏的 Logo 配色是按版式页头挑的。
           */
          setPhase('edit');
          setError(err instanceof ApiError ? err.message : String(err));
          return;
        }
      }
      setPhase('edit');
      setStep(3);
      return;
    }

    setPhase('creating');
    setError(null);
    setWarn(null);
    setProgress('正在创建站点…');

    let site: { siteId: string; host: string };
    try {
      site = await createSite(tenantId, trimmedName, {
        theme: chosen,
        /*
         * 对外名一起带上。建站这一刻会用它种首屏大标题与页面 `<title>`，
         * 之后再 PATCH 只改设置、改不了已经种进页面的那几处文字。
         * 三个候选依次退回：网站名称 → 公司名称 → 内部名。
         */
        title: trimmedTitle || trimmedCompany || undefined,
        /*
         * 主语言。这一步是它唯一能落库的时刻：页面是 `(site_id, locale, path)`
         * 一条记录，建完再换主语言等于整站重种，而 `setLocales` 明确不碰已有页面。
         * 服务端只在"这个租户还没有任何站"时才认它（见 site-create.repository）。
         */
        defaultLocale: siteLocale,
      });
    } catch (err) {
      /**
       * 写失败**不等于**没建出来。
       *
       * 2026-11-04 的真实案例：客户经 Cloudflare 回源，浏览器 20 秒先超时，
       * 服务端其实 40ms 就把站建好了。前端报"网络连接失败"，客户再点一次
       * 撞上 409 `site_limit`（名额已经被那个站占了）—— 他会以为彻底坏了，
       * 而站其实一直在「我的站点」里躺着。
       *
       * 所以失败后先回头查一遍：同名站已经在，就当成成功往下走。
       * 查不到才是真失败。
       */
      const recovered = await findCreatedSite();
      if (!recovered) {
        setPhase('edit');
        setError(err instanceof ApiError ? err.message : String(err));
        return;
      }
      setWarn('上一次请求没等到回应（网络慢），但站点其实已经建好了，已经帮你接上。');
      site = recovered;
    }

    setCreated({ siteId: site.siteId, host: site.host, theme: chosen });

    /**
     * 站点已经存在了。这一步之后的任何失败**都不能回滚**，
     * 也不能报"创建失败" —— 客户会再点一次，于是多出一个站。
     * 所以只记一条提示，继续往下走。
     */
    setProgress('正在保存公司信息…');
    try {
      const settings = await persistBasics(site.siteId);
      // PATCH 的返回里带着默认语言：第 5 屏靠它标出"哪一种是必选的"
      setDefaultLocale(settings.defaultLocale);
      setLocales([settings.defaultLocale]);
    } catch {
      setWarn('站点建好了，但公司信息没保存上（网络原因）。可以稍后在「站点信息」里补，不影响继续。');
      /**
       * 公司信息没存上，但**默认语言还是要拿到**。
       *
       * 拿不到就会用常量 `en` 顶着，而租户默认可能是中文 ——
       * 第 5 屏会把「English」标成默认、把简体中文当成可关的选项，
       * 客户照着点下去，站点的首页语言就和设置对不上了。
       */
      try {
        const settings = await fetchSiteSettings(tenantId, site.siteId);
        setDefaultLocale(settings.defaultLocale);
        setLocales([settings.defaultLocale]);
      } catch {
        /**
         * 连读都失败：退回**客户自己在第 1 步选的语言**，不是常量 `en`。
         *
         * 建站那一刻这个语言已经写进 `tenants.default_locale` 了，它才是
         * 站点真正的主语言。这里退回 `en` 的话，第 5 屏会把「English」标成
         * 默认、把中文当成可关的选项 —— 客户照着点，界面就和站点对不上了。
         */
        setDefaultLocale(siteLocale);
        setLocales([siteLocale]);
      }
    }

    setPhase('edit');
    setStep(3);
  };

  /* ── 第 4 步：Logo ──────────────────────────────────────── */

  const enterStepFive = async (): Promise<void> => {
    if (!created) {
      setStep(4);
      return;
    }
    if (!logoFile) {
      setStep(4);
      return;
    }
    setPhase('creating');
    setProgress('正在上传 Logo…');
    setError(null);
    try {
      const logoUrl = await uploadLogo(tenantId, created.siteId, logoFile);
      await saveSiteSettings(tenantId, created.siteId, { branding: { logoUrl } });
      pickLogo(null);
    } catch {
      /**
       * 站已经建好了，Logo 没上去不是失败。记一条提示继续走：
       * 拦在这里的话，客户会以为整件事都没成。
       */
      setWarn('Logo 没有传上去（文件可能太大或网络中断）。可以稍后在「站点信息 → 品牌」里补。');
    }
    setPhase('edit');
    setStep(4);
  };

  /* ── 第 5 步：完成 ──────────────────────────────────────── */

  const finish = async (): Promise<void> => {
    if (!created) return;
    /**
     * 只开了一种语言就不用发这条 PATCH：站点建出来时本来就是单语言，
     * 再发一次只是让「最后一步」多一次可能失败的请求。
     */
    if (locales.length > 1) {
      setPhase('creating');
      setProgress('正在开通语言…');
      setError(null);
      try {
        await saveSiteSettings(tenantId, created.siteId, { locales });
      } catch (err) {
        /**
         * 这一条**要拦**：语言没开成，客户以为开了，去翻译页会发现目标语言
         * 根本不存在。留在他重试，重试是安全的（PATCH 幂等）。
         */
        setPhase('edit');
        setError(err instanceof ApiError ? err.message : String(err));
        return;
      }
    }
    setPhase('done');
    /*
     * 走到完成屏就不再是"没保存的草稿"了：站点、Logo、语言都已经落库。
     * 不在这里清掉脏标记的话，客户从完成屏点侧栏还会被问"确定离开？"，
     * 而那个时候已经没有可以丢掉的东西。
     */
    onDirtyChange?.(false);
    await onDone();
  };

  /* ── 离开 ────────────────────────────────────────────────── */

  /**
   * 点「返回我的站点」。
   *
   * 「离开向导」有两条路：点侧栏（外壳拦）和点这一行返回（没人拦）。
   * 只做前者的话，客户填完名字顺手点一下返回就直接回到空态、一句话都没有，
   * 他会以为是页面出错。
   *
   * 站建好之后文案完全不同 —— 那时离开**不会**丢掉站，只是没配完，
   * 说成"放弃"会让他以为站没了。
   */
  const requestCancel = async (): Promise<void> => {
    if (phase === 'creating') return;
    if (created) {
      const ok = await confirm({
        title: '站点已经建好了，现在离开？',
        message:
          '它会留在「我的站点」里，不会丢。没走完的 Logo 和语言，之后在「站点信息」里随时能补。',
        confirmText: '先离开',
        cancelText: '继续配置',
      });
      if (!ok) return;
    } else if (trimmedName || trimmedTitle || trimmedCompany || trimmedBusiness || logoFile) {
      const ok = await confirm({
        title: '放弃现在填的内容？',
        message: '站还没建，这些内容都没有提交，离开后要重新填一遍。',
        confirmText: '放弃',
        cancelText: '继续填',
        tone: 'danger',
      });
      if (!ok) return;
    }
    onDirtyChange?.(false);
    onCancel();
  };

  const closeDone = (): void => {
    onDirtyChange?.(false);
    onCancel();
  };

  /**
   * 从完成屏去别的页。
   *
   * 先清脏标记再切页：不清的话，父层重挂向导（或下一次进来）会以为
   * 还有没提交的东西。**站已经建完了，这里确实没有可丢的东西。**
   */
  const leaveTo = (key: 'domains' | 'catalog'): void => {
    onDirtyChange?.(false);
    if (onNavigate) onNavigate(key);
    else onCancel();
  };

  /* ── 完成态 ──────────────────────────────────────────────── */

  if (phase === 'done' && created) {
    return (
      <WizardDone
        siteName={trimmedTitle || trimmedName}
        host={created.host}
        warn={warn}
        emailVerified={emailVerified}
        onClose={closeDone}
        onGo={leaveTo}
      />
    );
  }

  /* ── 编辑态 ──────────────────────────────────────────────── */

  const logoDone = logoAiUrl !== null || logoPreview !== null;

  /** 底部主按钮：第 3 步与第 5 步各有自己的异步动作 */
  const primary =
    step < 2
      ? () => setStep((s) => s + 1)
      : step === 2
        ? () => void enterStepFour()
        : step === 3
          ? () => void enterStepFive()
          : () => void finish();

  const primaryLabel =
    step === 2
      ? created
        ? '下一步'
        : '建站并继续'
      : step === 3
        ? logoFile
          ? '上传并继续'
          : '下一步'
        : step === 4
          ? '完成'
          : '下一步';

  return (
    <div className="wiz">
      <button
        type="button"
        className="wiz__back"
        onClick={() => void requestCancel()}
        disabled={phase === 'creating'}
      >
        ← 返回我的站点
      </button>

      <ol className="wiz__steps">
        {STEPS.map((label, index) => (
          <li
            key={label}
            className={`wiz__step${index === step ? ' wiz__step--on' : ''}${
              index < step ? ' wiz__step--done' : ''
            }`}
            aria-current={index === step ? 'step' : undefined}
          >
            {/*
              已走过的步骤可以点回去：客户在最后一屏改主意想换版式是常事。
              未到的步骤不给点 —— 「下一步」有自己的校验，跳过去就等于绕过它。
            */}
            <button
              type="button"
              className="wiz__stepbtn"
              disabled={index >= step || phase === 'creating'}
              onClick={() => setStep(index)}
            >
              <span className="wiz__dot">{index + 1}</span>
              <span className="wiz__steplabel">{label}</span>
            </button>
          </li>
        ))}
      </ol>

      {error ? <div className="notice notice--danger">{error}</div> : null}
      {warn && step >= 3 ? <div className="notice notice--warn">{warn}</div> : null}

      <div className="wiz__panel">
        {step === 0 ? (
          <>
            <h2 className="wiz__title">先给这个站起个名字</h2>
            <p className="wiz__hint">
              两个名字分工不同：上面那个只在控制台里用，下面那个是访客真正看到的。
            </p>

            <label className="field-block">
              <span>站点名称</span>
              <input
                className="field"
                type="text"
                value={siteName}
                maxLength={60}
                autoFocus
                placeholder="例如：北美市场 / Aurora US"
                onChange={(e) => setSiteName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canNext) setStep(1);
                }}
              />
            </label>
            <p className="wiz__tiny">
              只在控制台里区分多个站用，访客看不到。{siteName.length}/60
            </p>

            <label className="field-block">
              <span>网站名称</span>
              <input
                className="field"
                type="text"
                value={siteTitle}
                maxLength={SITE_TITLE_LIMIT}
                placeholder="例如：Aurora Tools"
                onChange={(e) => setSiteTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canNext) setStep(1);
                }}
              />
            </label>
            <p className="wiz__tiny">
              出现在页头、浏览器标签和搜索结果里。留空就跟着公司名走。
            </p>

            {/*
              主语言放在第一屏，而不是留给最后一步。

              它决定页面种在哪个语言下、起始内容用哪套话术 —— 这两件事都发生在
              「建站并继续」按下去的那一刻。放到第 5 步就晚了：那时候页面已经
              按默认语言种好了，而换主语言等于整站重种。
            */}
            <div className="field-block">
              <span>网站语言</span>
              <div className="wiz__langpick" role="radiogroup" aria-label="网站语言">
                {LOCALES.map((locale) => {
                  const on = siteLocale === locale;
                  return (
                    <label key={locale} className={`wiz-pick${on ? ' wiz-pick--on' : ''}`}>
                      <input
                        type="radio"
                        name="site-locale"
                        checked={on}
                        onChange={() => setSiteLocale(locale)}
                      />
                      <span className="wiz-pick__name">{LOCALE_LABEL[locale]}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <p className="wiz__tiny">
              网站内容用哪种语言写。起始页面的标题与提示都会跟着它 ——
              客户看不懂的语言，等于建完就废。以后要卖到别的国家，第 5 步能再加。
            </p>
          </>
        ) : null}

        {step === 1 ? (
          <>
            <h2 className="wiz__title">这家公司做什么</h2>
            <p className="wiz__hint">
              这几项都可以跳过，之后在「站点信息」里补。填了会让后面的 AI 生成更准 ——
              它知道你是做卡尺的还是做阀门的，画出来的东西才不一样。
            </p>

            <label className="field-block">
              <span>公司名称</span>
              <input
                className="field"
                type="text"
                value={company}
                maxLength={SITE_COMPANY_LIMIT}
                autoFocus
                placeholder="例如：宁波欧若拉精密工具有限公司"
                onChange={(e) => setCompany(e.target.value)}
              />
            </label>
            <p className="wiz__tiny">用在页脚版权与搜索引擎的公司信息里。留空就跟着租户名走。</p>

            <label className="field-block">
              <span>主营业务</span>
              <input
                className="field"
                type="text"
                value={business}
                maxLength={SITE_BUSINESS_LIMIT}
                placeholder="一句话说清你做什么、卖什么"
                onChange={(e) => setBusiness(e.target.value)}
              />
            </label>

            <label className="field-block">
              <span>邮箱</span>
              <input
                className="field"
                type="email"
                value={email}
                autoComplete="off"
                placeholder="sales@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>

            <div className="wiz__pair">
              <label className="field-block">
                <span>电话</span>
                <input
                  className="field"
                  type="tel"
                  value={phone}
                  autoComplete="off"
                  placeholder="+86 574 8888 6666"
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
              <label className="field-block">
                <span>WhatsApp</span>
                <input
                  className="field"
                  type="tel"
                  value={whatsapp}
                  autoComplete="off"
                  placeholder="8613800138000"
                  onChange={(e) => setWhatsapp(e.target.value)}
                />
              </label>
            </div>
            {whatsappBad ? (
              <p className="wiz__err">
                这个号码用不了：请带上国家码、只填数字，不要开头的 0（例如 8613800138000）。
              </p>
            ) : whatsappDigits ? (
              <p className="wiz__tiny">前台会生成这个链接：wa.me/{whatsappDigits}</p>
            ) : (
              <p className="wiz__tiny">
                号码要带国家码（中国是 86）。填了它，访客点一下就能在 WhatsApp 里跟你说话。
              </p>
            )}
          </>
        ) : null}

        {step === 2 ? (
          <>
            <h2 className="wiz__title">选个版式</h2>
            <p className="wiz__hint">
              版式决定页面的骨架和配色，内容都是你自己的。现在选一个，之后想换随时能换。
            </p>
            {themeError ? (
              <div className="notice notice--danger">版式列表没拉下来：{themeError}</div>
            ) : themes === null ? (
              <p className="muted">加载中…</p>
            ) : themes.length === 0 ? (
              <div className="notice notice--warn">
                暂时拿不到可用的版式，先按基础版「极光」建站，回头在「主题市场」里再选。
              </div>
            ) : (
              <div className="wiz__themes">
                {themes.map((item) => {
                  const on = item.name === theme;
                  return (
                    <button
                      key={item.name}
                      type="button"
                      className={`wiz-theme${on ? ' wiz-theme--on' : ''}`}
                      aria-pressed={on}
                      onClick={() => setTheme(item.name)}
                    >
                      <span className="wiz-theme__art">
                        {item.previewCardUrl ? (
                          <img src={item.previewCardUrl} alt={`${item.labelZh} 预览`} loading="lazy" />
                        ) : summaries.get(item.name) ? (
                          <ThemeThumb theme={summaries.get(item.name)!} />
                        ) : (
                          <span className="wiz-theme__nofig">暂无预览图</span>
                        )}
                      </span>
                      <span className="wiz-theme__body">
                        <span className="wiz-theme__name">{item.labelZh}</span>
                        <span className="wiz-theme__desc">{item.descriptionZh || item.descriptionEn}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {/*
              建站发生在这一步之后，所以按钮文案必须说出来。
              写成「下一步」的话，客户点下去以为只是翻页，其实站已经落库了 ——
              之后他再点「返回」看到的确认弹窗会显得莫名其妙。
            */}
            <p className="wiz__tiny">
              点「{created ? '下一步' : '建站并继续'}」就会把站建出来，接着配 Logo 和语言。
              更多版式在「主题市场」，建好站之后随时能换。
            </p>
          </>
        ) : null}

        {step === 3 ? (
          <>
            <h2 className="wiz__title">加个 Logo（可以跳过）</h2>
            <p className="wiz__hint">
              有现成的就传上来；没有的话，右边那个按钮会按你填的公司信息画几个给你挑。
            </p>

            <div className="wiz__logo">
              <div className="wiz__logo-slot">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo 预览" />
                ) : logoAiUrl ? (
                  <img src={logoAiUrl} alt="AI 生成的 Logo" />
                ) : (
                  <span className="wiz__logo-empty">还没有图</span>
                )}
              </div>
              <div className="wiz__logo-side">
                <input
                  ref={fileRef}
                  type="file"
                  accept={MEDIA_MIME.join(',')}
                  className="wiz__file"
                  onChange={(e) => {
                    pickLogo(e.target.files?.[0] ?? null);
                    // 清掉 value：同一个文件选第二次也要触发 change
                    e.target.value = '';
                  }}
                />
                <div className="wiz__logo-actions">
                  <Button variant="ghost" onClick={() => fileRef.current?.click()}>
                    {logoDone ? '换一张' : '选择图片'}
                  </Button>
                  <Button variant="ghost" onClick={() => setBrandOpen(true)}>
                    用 AI 生成
                  </Button>
                  {logoDone ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        pickLogo(null);
                        setLogoAiUrl(null);
                      }}
                    >
                      不用了
                    </Button>
                  ) : null}
                </div>
                <p className="wiz__tiny">
                  建议 PNG 透明底、正方形，至少 256×256。JPG / WebP / AVIF 也可以。
                </p>
                {logoError ? <p className="wiz__err">{logoError}</p> : null}
              </div>
            </div>

            <div className="wiz__skip">
              没有 Logo 也不影响建站：页头会显示<b>网站名称的文字</b>，一样能看。之后随时能补。
            </div>
          </>
        ) : null}

        {step === 4 ? (
          <>
            <h2 className="wiz__title">开通语言</h2>
            <p className="wiz__hint">
              每种语言是一套独立的页面与商品文案。开通之后可以用「多语言翻译」从默认语言整站翻过去，
              不用重写一遍。现在只开一种也完全可以，之后在「站点信息」里加。
            </p>

            <div className="wiz__langs">
              {LOCALES.map((locale) => {
                const on = locales.includes(locale);
                const isDefault = locale === defaultLocale;
                return (
                  <label
                    key={locale}
                    className={`wiz-lang${on ? ' wiz-lang--on' : ''}${isDefault ? ' wiz-lang--fixed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      /*
                       * 默认语言锁死。它不是一个"偏好"，是整站入口：
                       * 关掉它 `/` 会直接 404，而客户几乎不会预期这个结果 ——
                       * 服务端也会拒（`至少要保留一种语言` / 默认语言必须在列表里）。
                       */
                      disabled={isDefault}
                      onChange={() =>
                        setLocales((current) =>
                          on ? current.filter((l) => l !== locale) : [...current, locale],
                        )
                      }
                    />
                    <span className="wiz-lang__name">{LOCALE_LABEL[locale]}</span>
                    {isDefault ? <span className="wiz-lang__tag">默认</span> : null}
                  </label>
                );
              })}
            </div>

            <p className="wiz__tiny">
              默认语言是 {LOCALE_LABEL[defaultLocale]}，它不能被关掉 —— 关掉等于关掉首页。
            </p>
          </>
        ) : null}
      </div>

      <div className="wiz__foot">
        <Button
          variant="ghost"
          disabled={phase === 'creating' || step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          上一步
        </Button>
        <Button variant="primary" disabled={!canNext || phase === 'creating'} onClick={primary}>
          {phase === 'creating' ? progress || '处理中…' : primaryLabel}
        </Button>
      </div>

      {phase === 'creating' ? (
        /*
         * 遮罩挡住整块。挡住的不只是"再点一次"，还有"点侧栏去别页"——
         * 请求已经发出去了，这时候切页只会让客户以为没成，
         * 回头再建一次就多出一个站。
         */
        <div className="wiz__veil" role="status" aria-live="polite">
          <div className="wiz__veilbox">
            <div className="wiz__spinner" aria-hidden="true" />
            <p className="wiz__veiltext">{progress || '处理中…'}</p>
          </div>
        </div>
      ) : null}

      {/*
        AI 出图放在这一屏，是因为此刻 siteId 已经有了（第 3 步建的）。
        `brand_art_jobs.site_id` 是 not null —— 在建站之前这一屏根本挂不了任务，
        这也是建站要提前到第 3 步之后的直接原因。
      */}
      {created ? (
        <BrandGenerator
          open={brandOpen}
          onClose={() => setBrandOpen(false)}
          tenantId={tenantId}
          siteId={created.siteId}
          /* 用对外站名而不是内部叫法：模型要画的是访客认识的那个牌子 */
          siteName={trimmedTitle || trimmedCompany || trimmedName}
          brandColor={null}
          /* 标最终压在页头上，配色按这套版式的页头底色挑；拿不到就退回基础版极光 */
          headerBg={
            summaries.get(theme ?? '')?.palette.headerBg ??
            summaries.get('aurora')?.palette.headerBg ??
            ''
          }
          onApply={({ logoUrl, faviconUrl }) => {
            void saveSiteSettings(tenantId, created.siteId, {
              branding: { logoUrl, faviconUrl },
            }).catch(() => {
              // 保存失败不该把已经关掉的弹窗再弹回来 —— 下一屏的提示与「站点信息」都能兜住
            });
            // AI 出的图已经落库了，本地再选的文件就没有意义，清掉
            pickLogo(null);
            setLogoAiUrl(logoUrl);
            setBrandOpen(false);
          }}
        />
      ) : null}

      {confirmDialog}
    </div>
  );
}
